/**
 * 이용률 순위 — 무엇이 더 쓰이는가 (DESIGN §8.9).
 *
 * **가동률 순위와 같은 골격에 재는 것만 바꾼다.** 두 화면을 오갈 때 읽는 법을
 * 다시 배우지 않아도 되게 하려는 것이다.
 *
 * **신뢰구간을 그리지 않는다.** Wilson 은 이항 비율의 구간이다. 가동률은
 * "관측된 분 중 UP 인 분"이라 이항으로 근사할 근거가 있지만, 이용률은 점유
 * 시간의 비율이라 **시행 횟수가 정의되지 않는다.** 근거 없는 구간을 그리는
 * 것은 §8.6.5 가 금지한 거짓 정밀도다. 대신 관측 시간을 함께 노출해 근거의
 * 두께를 보인다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { fetchEnvelope } from '@/lib/api'
import { axisStyle, CHART_H } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, minutes, pct } from '@/lib/fmt'
import type { Envelope, RankEntity, RankingData, RankingItem } from '@/lib/types'
import { RANK_ENTITIES } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

/** 표본이 얇은 칸은 버리지 않고 아래로 내린다 (순위의 excluded 와 같은 문법) */
const MIN_CHARGERS = 30

interface Props {
  entity: RankEntity
}

export function UtilizationRanking({ entity }: Props) {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['metrics/ranking', 'util', entity, filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<RankingData>(
        '/metrics/ranking',
        toQueryFilters(filters),
        { entity },
        signal,
      ),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<RankingData> | undefined

  /**
   * **순위·제외를 합쳐서 쓴다.** `excluded` 는 *가동률 비교* 기준으로 갈린
   * 것이다 — 관측 창이 28일 미만이면 비교 보류다. 그런데 이용률은 비교
   * 가능성 판정 대상이 아니므로 그 기준으로 버리면 화면이 통째로 빈다
   * (실측: 사업자 146곳 전부 excluded). 대신 **대수가 얇은 칸만** 분리한다.
   */
  const { rows, thin } = useMemo(() => {
    const all: RankingItem[] = [...(env?.data.ranking ?? []), ...(env?.data.excluded ?? [])]
    const usable = all.filter((r) => r.utilization != null && r.entity_id)
    return {
      rows: usable
        .filter((r) => r.n_chargers >= MIN_CHARGERS)
        .sort((a, b) => (b.utilization ?? 0) - (a.utilization ?? 0)),
      thin: usable.filter((r) => r.n_chargers < MIN_CHARGERS),
    }
  }, [env])

  const label = RANK_ENTITIES.find((e) => e.key === entity)?.label ?? ''

  const option = useMemo<EChartsOption>(() => {
    const c1 = cssVar('--c-cat-1', '#0066cc')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const shown = rows.slice(0, 20)

    return {
      grid: { left: 96, right: 56, top: 28, bottom: 36 },
      tooltip: {
        trigger: 'item',
        formatter: (p) => {
          const r = shown[(p as { dataIndex: number }).dataIndex]
          if (!r) return ''
          return `<b>${r.entity_id}</b><br/>이용률 ${pct(r.utilization)}<br/>충전기 ${int(
            r.n_chargers,
          )}기<br/>관측 ${minutes(r.t_obs_min)}`
        },
      },
      xAxis: {
        type: 'value',
        min: 0,
        name: '이용률 (%)',
        nameLocation: 'middle',
        nameGap: 22,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: (v: number) => `${(v * 100).toFixed(0)}` },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: shown.map((r) => r.entity_id),
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, width: 88, overflow: 'truncate' },
      },
      series: [
        {
          type: 'bar',
          data: shown.map((r) => r.utilization ?? 0),
          // 데이터 끝만 둥글게 · 막대 사이 2px 표면 간격
          itemStyle: { color: c1, borderRadius: [0, 4, 4, 0] },
          barCategoryGap: '30%',
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            color: cssVar('--c-ink-2', '#4a4a4f'),
            formatter: (p) => pct(Number(p.value), 1),
          },
        },
      ],
    }
  }, [rows])

  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">{label}별 이용률</caption>
        <thead>
          <tr>
            <th scope="col">{label}</th>
            <th scope="col">이용률</th>
            <th scope="col">충전기</th>
            <th scope="col">관측 시간</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.entity_id}>
              <th scope="row">{r.entity_id}</th>
              <td className="num">{pct(r.utilization)}</td>
              <td className="num">{int(r.n_chargers)}</td>
              <td className="num">{minutes(r.t_obs_min)}</td>
            </tr>
          ))}
          {thin.map((r) => (
            <tr key={r.entity_id} className="is-na">
              <th scope="row">{r.entity_id}</th>
              <td className="num">{pct(r.utilization)}</td>
              <td className="num">{int(r.n_chargers)}</td>
              <td>충전기 {MIN_CHARGERS}기 미만 — 순위에서 분리</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title={`${label}별 이용률`}
      question="관측된 시간 중 실제로 충전에 쓰인 비중 — 무엇이 더 쓰이는가"
      meta={env?.meta}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={rows.length === 0}
      onRetry={() => void q.refetch()}
      headline={
        rows.length > 0 ? (
          <>
            <span className="num">{pct(rows[0]?.utilization, 1)}</span>
            <span className="cframe__headnote">최고 · {rows[0]?.entity_id}</span>
          </>
        ) : null
      }
      encoding={[
        { role: '가로축', what: '이용률 (%)' },
        { role: '세로축', what: `${label} · 이용률 내림차순` },
        { role: '막대 길이', what: '충전 상태(stat=3) 시간 / 관측 시간' },
      ]}
      reading="관측된 시간 중 충전중이었던 비중입니다. 가동률과 달리 '설비가 멀쩡한가'가 아니라 '실제로 쓰이는가'를 잽니다."
      notes={[
        {
          label: '신뢰구간이 없는 이유',
          text:
            '이용률은 점유 시간의 비율이라 이항 시행이 정의되지 않습니다. Wilson 구간을 그리면 근거 없는 정밀도가 됩니다 — 대신 표 보기에서 관측 시간으로 근거의 두께를 확인하세요.',
        },
        {
          label: '분모',
          text:
            '관측 시간(t_obs)입니다. 고장 안 난 시간(t_up)을 분모로 쓰면 고장이 많은 설비의 이용률이 올라가 사용량 지표가 고장 지표를 타고 움직입니다.',
        },
        ...(thin.length > 0
          ? [
              {
                label: '분리 표시',
                text: `충전기 ${MIN_CHARGERS}기 미만 ${thin.length}칸은 차트에서 빼고 표 보기 아래쪽에 회색으로 남겼습니다.`,
              },
            ]
          : []),
        ...(rows.length > 20 ? [`상위 20칸만 그렸습니다 (전체 ${rows.length}칸은 표 보기).`] : []),
      ]}
      table={table}
    >
      <EChart option={option} height={CHART_H} ariaLabel={`${label}별 이용률 막대그래프`} />
    </ChartFrame>
  )
}
